import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { supabaseClient } from '../../services/supabase';

interface HeatPumpPerCapitaChartProps {
  location: string;
  geofips: number;
}

export const HeatPumpPerCapitaChart: React.FC<HeatPumpPerCapitaChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerCapitaData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch heat pump data for location and California, and population data
        const [locationData, californiaData, locationPop, californiaPop] = await Promise.all([
          supabaseClient
            .from('heat_pumps')
            .select('year, units_installed')
            .eq('geo_fips', geofips)
            .order('year', { ascending: true }),
          supabaseClient
            .from('heat_pumps')
            .select('year, units_installed')
            .eq('geo_fips', 6) // California geofips
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', 6) // California geofips
            .order('year', { ascending: true })
        ]);

        if (locationData.error) {
          console.error('Error fetching location heat pump data:', locationData.error);
          throw locationData.error;
        }
        if (californiaData.error) {
          console.error('Error fetching California heat pump data:', californiaData.error);
          throw californiaData.error;
        }
        if (locationPop.error) {
          console.error('Error fetching location population data:', locationPop.error);
          throw locationPop.error;
        }
        if (californiaPop.error) {
          console.error('Error fetching California population data:', californiaPop.error);
          throw californiaPop.error;
        }

        // Aggregate units by year for location
        const locationByYear = new Map<number, number>();
        locationData.data?.forEach(record => {
          const year = record.year;
          locationByYear.set(year, (locationByYear.get(year) || 0) + (record.units_installed || 0));
        });

        // Aggregate units by year for California
        const californiaByYear = new Map<number, number>();
        californiaData.data?.forEach(record => {
          const year = record.year;
          californiaByYear.set(year, (californiaByYear.get(year) || 0) + (record.units_installed || 0));
        });

        // Create population maps
        const locationPopMap = new Map<number, number>();
        locationPop.data?.forEach(record => {
          locationPopMap.set(record.year, record.population || 0);
        });

        const californiaPopMap = new Map<number, number>();
        californiaPop.data?.forEach(record => {
          californiaPopMap.set(record.year, record.population || 0);
        });

        // Get all years
        const allYears = new Set([
          ...Array.from(locationByYear.keys()),
          ...Array.from(californiaByYear.keys()),
          ...Array.from(locationPopMap.keys()),
          ...Array.from(californiaPopMap.keys())
        ]);

        // Find the most recent year with data
        const latestYear = Math.max(...Array.from(allYears).filter(y => y <= new Date().getFullYear()));

        // Calculate per 10,000 residents for latest year
        const locationUnits = locationByYear.get(latestYear) || 0;
        const californiaUnits = californiaByYear.get(latestYear) || 0;
        const locationPopulation = locationPopMap.get(latestYear) || 0;
        const californiaPopulation = californiaPopMap.get(latestYear) || 0;

        const locationPer10000 = locationPopulation > 0 ? (locationUnits / locationPopulation) * 10000 : 0;
        const californiaPer10000 = californiaPopulation > 0 ? (californiaUnits / californiaPopulation) * 10000 : 0;

        // Create chart
        if (chartRef.current) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: [location, 'California Average'],
                datasets: [{
                  label: `Heat Pumps per 10,000 Residents (${latestYear})`,
                  data: [locationPer10000, californiaPer10000],
                  backgroundColor: ['#3b82f6', '#10b981'],
                  borderColor: ['#2563eb', '#059669'],
                  borderWidth: 2
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: `Heat Pumps per 10,000 Residents - ${location} vs California (${latestYear})`,
                    font: { size: 16, weight: 'bold' }
                  },
                  legend: {
                    display: false
                  },
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const value = context.parsed.y;
                        return `${value.toFixed(2)} heat pumps per 10,000 residents`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Location',
                      font: { size: 14, weight: 'bold' }
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Heat Pumps per 10,000 Residents',
                      font: { size: 14, weight: 'bold' }
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value: any) => `${value.toFixed(2)}`
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching heat pump per capita data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load heat pump per capita data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerCapitaData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading heat pump per capita comparison data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '380px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};


